import { useMediaQuery, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { Navigate } from 'react-router-dom';
import { deleteConfirmation, getConfirmation } from '../api/epharm-api';
import EpharmCard from '../components/epharm-card';
import EpharmLogo from '../components/epharm-logo';
import Loader from '../components/loader';
import CollectionAddress from '../components/steps/collection-address';
import DeliveryAddress from '../components/steps/delivery-address';
import Payment from '../components/steps/payment';
import ProcessComplete from '../components/steps/process-complete';
import YourItems from '../components/steps/your-items';
import type { Confirmation } from '../domain/epharm';
import { ColorlibConnector, ColorlibStepIcon } from '../styles';

const steps = ['Your Items', 'Collection Address', 'Delivery Address', 'Payment'];

function Home() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code') ?? '';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));

  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [confirmationData, setConfirmationData] = useState<Confirmation>({
    order_id: '',
    delivery_address: {
      line_1: '',
      line_2: '',
      city: '',
      state: '',
      country: '',
      postcode: '',
    },
  });
  const [isExpired, setIsExpired] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchConfirmationData = async () => {
      try {
        const confirmation = await getConfirmation(code);
        setConfirmationData(confirmation);
      } catch (_error) {
        setIsExpired(true);
      } finally {
        setIsInitialized(true);
      }
    };

    fetchConfirmationData();
  }, [code]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await deleteConfirmation(code);
      setIsCompleted(true);
    } catch (_error) {
      toast.error('Something went wrong while confirming your details. Please try again later.', {
        duration: 3000,
        style: {
          boxShadow: 'none',
          border: '1px solid #D32F2F',
          fontSize: '1.5vh',
          color: '#D32F2F',
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === steps.length - 1) {
      return handleConfirm();
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  if (isExpired) {
    return <Navigate to="/page-not-found" />;
  }

  if (!isInitialized) {
    return null;
  }

  return (
    <>
      <Toaster />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '100%',
          maxWidth: 700,
          height: '100%',
          margin: '1% auto',
          alignItems: 'center',
        }}
      >
        <EpharmLogo />

        {isLoading ? (
          <EpharmCard content={<Loader />} />
        ) : !hasStarted ? (
          <EpharmCard
            nextButtonText="Next"
            onNext={() => setHasStarted(true)}
            content={
              <div className="center-items">
                <p
                  style={{
                    textAlign: 'center',
                    fontSize: '19px',
                    fontWeight: 'bold',
                    padding: '0 3%',
                  }}
                >
                  Book your nominated courier (DPD) to collect your ePharmNI items
                </p>
                <p
                  style={{
                    textAlign: 'center',
                    color: '#868080',
                    fontSize: '15.5px',
                    margin: 0,
                    padding: '0 6%',
                  }}
                >
                  Please follow the steps to confirm your collection and delivery address
                </p>
              </div>
            }
          />
        ) : !isCompleted ? (
          <>
            <Stepper
              activeStep={currentStep}
              sx={{
                width: isMobile ? '108%' : '45vw',
                mb: 3,
              }}
              alternativeLabel
              connector={<ColorlibConnector />}
            >
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel slots={{ stepIcon: ColorlibStepIcon }} sx={{ fontSize: 200 }}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <EpharmCard
              nextButtonText={currentStep === steps.length - 1 ? 'Confirm' : 'Next'}
              previousButtonText="Previous"
              onNext={goToNextStep}
              onPrevious={goToPreviousStep}
              previousButtonDisabled={currentStep === 0}
              content={
                <>
                  {currentStep === 0 && <YourItems />}
                  {currentStep === 1 && <CollectionAddress />}
                  {currentStep === 2 && <DeliveryAddress address={confirmationData.delivery_address} />}
                  {currentStep === 3 && <Payment />}
                </>
              }
            />
          </>
        ) : (
          <EpharmCard content={<ProcessComplete />} />
        )}
      </Box>
    </>
  );
}

export default Home;
